import React, { useEffect, useState } from 'react';

// Helper function to generate image array for a project
const generateGallery = (basePath, count, extension = 'png') => {
  const images = [];
  for (let i = 1; i <= count; i++) {
    images.push(`${basePath}/${i}.${extension}`);
  }
  return images;
};

function GalleryImage({ src, alt, imageKey, className = '' }) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [imageKey]);

  return (
    <div className={`overflow-hidden border border-white/5 aspect-[4/3] relative group ${className}`}>
      {!loaded && <div className="gallery-skeleton absolute inset-0 z-10" aria-hidden="true" />}
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        className={`w-full h-full object-cover grayscale hover:grayscale-0 transition-[opacity,filter,transform] duration-500 hover:scale-105 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  );
}

function ProjectDetailPageNew({ projectId, onBack }) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [projectId]);

  const projectsData = [
    // Construction Projects
    {
      title: "Khajekhalan Ghat Patna",
      location: "Patna, Bihar",
      meta: "SECTOR: CONSTRUCTION | TYPE: RIVERFRONT DEVELOPMENT",
      description: "Riverfront development project enhancing infrastructure and amenities at Patna ghat, improving public accessibility and environmental sustainability.",
      gallery: generateGallery("/assets/RIPL Projects/Construction/Khajekhalan Ghat Patna", 9)
    },
    {
      title: "Khajuraho Landscape Development",
      location: "Khajuraho, Madhya Pradesh",
      meta: "SECTOR: CONSTRUCTION | TYPE: LANDSCAPE DEVELOPMENT",
      description: "Landscape development project enhancing the aesthetic appeal of the historic Khajuraho region while preserving cultural heritage.",
      gallery: generateGallery("/assets/RIPL Projects/Construction/Khajuraho Landscape Development", 11)
    },
    {
      title: "LIC Apartments, Canal Street",
      location: "Canal Street, Kolkata",
      meta: "SECTOR: CONSTRUCTION | TYPE: RESIDENTIAL",
      description: "Modern residential complex with sustainable design and contemporary amenities for LIC policy holders.",
      gallery: generateGallery("/assets/RIPL Projects/Construction/LIC Apartments, Canal Street, Kolkata", 3)
    },
    {
      title: "LIC Bhagalpur",
      location: "Bhagalpur, Bihar",
      meta: "SECTOR: CONSTRUCTION | TYPE: COMMERCIAL",
      description: "Commercial office development for LIC operations in Bhagalpur with modern facilities.",
      gallery: generateGallery("/assets/RIPL Projects/Construction/LIC Bhagalpur, Bihar", 2)
    },
    {
      title: "LIC Divisional Office, Udaipur",
      location: "Udaipur, Rajasthan",
      meta: "SECTOR: CONSTRUCTION | TYPE: COMMERCIAL",
      description: "Modern office complex with traditional Rajasthani architectural influences, blending heritage with contemporary design.",
      gallery: generateGallery("/assets/RIPL Projects/Construction/LIC DO, Udaipur", 2)
    },
    {
      title: "LIC Mixed Use Development, Vadodara",
      location: "Vadodara, Gujarat",
      meta: "SECTOR: CONSTRUCTION | TYPE: MIXED-USE",
      description: "Integrated mixed-use development combining commercial and residential spaces with modern infrastructure.",
      gallery: [
        ...generateGallery("/assets/RIPL Projects/Construction/LIC Mixed Use Development, Vadodara, Gujarat", 4),
        "/assets/RIPL Projects/Construction/LIC Mixed Use Development, Vadodara, Gujarat/5.jpg"
      ]
    },
    {
      title: "LIC Policy Holders Housing",
      location: "Kolkata, West Bengal",
      meta: "SECTOR: CONSTRUCTION | TYPE: RESIDENTIAL",
      description: "Residential housing project designed for LIC policy holders with modern amenities and sustainable features.",
      gallery: generateGallery("/assets/RIPL Projects/Construction/LIC Policy Holders Housing, Kolkata", 2)
    },
    {
      title: "LIC ZTC Jamshedpur",
      location: "Jamshedpur, Jharkhand",
      meta: "SECTOR: CONSTRUCTION | TYPE: INSTITUTIONAL",
      description: "Zonal Training Centre with modern training facilities and infrastructure for professional development.",
      gallery: generateGallery("/assets/RIPL Projects/Construction/LIC ZTC, Jamshedpur, Jharkhand", 2)
    },
    {
      title: "LIC Office, Satara",
      location: "Satara, Maharashtra",
      meta: "SECTOR: CONSTRUCTION | TYPE: COMMERCIAL",
      description: "Regional office development with contemporary design and efficient space planning.",
      gallery: generateGallery("/assets/RIPL Projects/Construction/LIC, Satara, Maharashtra", 3)
    },
    {
      title: "National Institute of Oceanography",
      location: "Goa",
      meta: "SECTOR: CONSTRUCTION | TYPE: RESEARCH FACILITY",
      description: "State-of-the-art research facility designed for marine science and oceanographic studies with specialized infrastructure.",
      gallery: generateGallery("/assets/RIPL Projects/Construction/National Institute of Oceanography, Goa", 3)
    },
    {
      title: "Tourist Infrastructure, Kanheri Caves",
      location: "Mumbai, Maharashtra",
      meta: "SECTOR: CONSTRUCTION | TYPE: HERITAGE INFRASTRUCTURE",
      description: "Heritage-sensitive infrastructure development enhancing visitor experience at Kanheri Caves while preserving archaeological significance.",
      gallery: generateGallery("/assets/RIPL Projects/Construction/Tourist Infrastructure Facilities, Kanheri Caves, Mumbai", 13)
    },
    // Interiors Projects
    {
      title: "14th Floor, State Bank Bhavan",
      location: "Mumbai, Maharashtra",
      meta: "SECTOR: INTERIORS | TYPE: CORPORATE",
      description: "Executive floor interiors with premium finishes and modern workspace design for banking operations.",
      gallery: generateGallery("/assets/RIPL Projects/Interiors/14 Floor, State Bank Bhavan, Mumbai", 6)
    },
    {
      title: "Income Tax Office",
      location: "Swargate, Pune",
      meta: "SECTOR: INTERIORS | TYPE: GOVERNMENT",
      description: "Government office interiors designed for efficiency and public accessibility with modern facilities.",
      gallery: generateGallery("/assets/RIPL Projects/Interiors/Income Tax Office, Swargate, Pune", 8)
    },
    {
      title: "NABARD Office Interiors",
      location: "Pune, Maharashtra",
      meta: "SECTOR: INTERIORS | TYPE: CORPORATE",
      description: "Professional workspace with modern amenities and efficient layout planning for agricultural banking operations.",
      gallery: generateGallery("/assets/RIPL Projects/Interiors/NABARD, Pune", 14)
    },
    {
      title: "SBI Auditorium",
      location: "Mumbai, Maharashtra",
      meta: "SECTOR: INTERIORS | TYPE: AUDITORIUM",
      description: "Multi-purpose auditorium with advanced acoustics and state-of-the-art facilities for corporate events.",
      gallery: generateGallery("/assets/RIPL Projects/Interiors/SBI Audiotorium, Mumbai", 9)
    },
    {
      title: "SBI Conference Center",
      location: "Mumbai, Maharashtra",
      meta: "SECTOR: INTERIORS | TYPE: CONFERENCE FACILITY",
      description: "State-of-the-art conference facilities with advanced AV systems and elegant finishes for executive meetings.",
      gallery: generateGallery("/assets/RIPL Projects/Interiors/SBI Conference Center, State Bank Bhavan, Mumbai", 17)
    },
    {
      title: "SBI General Insurance",
      location: "Thane, Maharashtra",
      meta: "SECTOR: INTERIORS | TYPE: CORPORATE",
      description: "Corporate office interiors with modern design and ergonomic workspace planning for insurance operations.",
      gallery: generateGallery("/assets/RIPL Projects/Interiors/SBI General Insurance, Thane", 10)
    },
    {
      title: "SBI Local Head Office",
      location: "BKC, Mumbai",
      meta: "SECTOR: INTERIORS | TYPE: CORPORATE",
      description: "Contemporary office interiors designed for enhanced productivity and employee comfort in the financial district.",
      gallery: generateGallery("/assets/RIPL Projects/Interiors/SBI LHO BKC, Mumbai", 5)
    },
    {
      title: "SBI Life Insurance, Andheri",
      location: "Andheri, Mumbai",
      meta: "SECTOR: INTERIORS | TYPE: CORPORATE",
      description: "Modern office interiors with customer-centric design and efficient workflow for insurance services.",
      gallery: [
        ...generateGallery("/assets/RIPL Projects/Interiors/SBI Life Insurance, Andheri", 5),
        "/assets/RIPL Projects/Interiors/SBI Life Insurance, Andheri/Gemini_Generated_Image_21uynd21uynd21uy.png"
      ]
    },
    {
      title: "SBI Life Insurance, Guwahati",
      location: "Guwahati, Assam",
      meta: "SECTOR: INTERIORS | TYPE: CORPORATE",
      description: "Regional office interiors with contemporary design and local cultural elements for northeast operations.",
      gallery: generateGallery("/assets/RIPL Projects/Interiors/SBI Life Insurance, Guwahati", 12)
    },
    {
      title: "SBI Mutual Fund",
      location: "BKC, Mumbai",
      meta: "SECTOR: INTERIORS | TYPE: FINANCIAL SERVICES",
      description: "Premium financial services office with sophisticated design and client meeting areas in the business district.",
      gallery: [
        "/assets/RIPL Projects/Interiors/SBI Mutual Fund BKC, Mumbai/1.png",
        "/assets/RIPL Projects/Interiors/SBI Mutual Fund BKC, Mumbai/2.png",
        "/assets/RIPL Projects/Interiors/SBI Mutual Fund BKC, Mumbai/3.png",
        "/assets/RIPL Projects/Interiors/SBI Mutual Fund BKC, Mumbai/5.png",
        "/assets/RIPL Projects/Interiors/SBI Mutual Fund BKC, Mumbai/6.png",
        "/assets/RIPL Projects/Interiors/SBI Mutual Fund BKC, Mumbai/7.png",
        "/assets/RIPL Projects/Interiors/SBI Mutual Fund BKC, Mumbai/8.png",
        "/assets/RIPL Projects/Interiors/SBI Mutual Fund BKC, Mumbai/Gemini_Generated_Image_fjoffrfjoffrfjof.png"
      ]
    },
    {
      title: "SBI Reception Lobby",
      location: "Mumbai, Maharashtra",
      meta: "SECTOR: INTERIORS | TYPE: CORPORATE LOBBY",
      description: "Impressive reception area reflecting the corporate identity with modern design elements and premium finishes.",
      gallery: generateGallery("/assets/RIPL Projects/Interiors/SBI Reception Lobby, State Bank Bhavan, Mumbai", 14)
    },
    {
      title: "SBI Wealth Management",
      location: "Mumbai, Maharashtra",
      meta: "SECTOR: INTERIORS | TYPE: WEALTH MANAGEMENT",
      description: "Premium interior spaces designed for wealth management services with sophisticated finishes and private consultation areas.",
      gallery: generateGallery("/assets/RIPL Projects/Interiors/SBI Wealth Management, Air India Building, Mumbai", 10)
    },
    {
      title: "Sports Facilities, MDL",
      location: "Mazagaon, Mumbai",
      meta: "SECTOR: INTERIORS | TYPE: RECREATIONAL",
      description: "Employee sports and recreation facilities with modern amenities and design for dock workers.",
      gallery: generateGallery("/assets/RIPL Projects/Interiors/Sports Facilities, Mazagaon Docks Limited, Mumbai", 3)
    },
    {
      title: "UTIITSL Office",
      location: "Thane, Maharashtra",
      meta: "SECTOR: INTERIORS | TYPE: CORPORATE",
      description: "Technology service office with contemporary design and collaborative workspaces for IT operations.",
      gallery: generateGallery("/assets/RIPL Projects/Interiors/UTIITSL, Thane", 7)
    },
    {
      title: "Visitor Facility Center",
      location: "Khajuraho, Madhya Pradesh",
      meta: "SECTOR: INTERIORS | TYPE: HERITAGE FACILITY",
      description: "Culturally sensitive interior design for heritage site visitor facilities at Khajuraho monuments.",
      gallery: generateGallery("/assets/RIPL Projects/Interiors/Visitor Facility Center, Khajuraho Group of Monuments", 6)
    },
    // Retrofitting Projects
    {
      title: "Bank of Maharashtra",
      location: "Shivaji Nagar, Pune",
      meta: "SECTOR: RETROFITTING | TYPE: SEISMIC UPGRADE",
      description: "Seismic retrofitting and modernization of heritage banking facility ensuring structural safety.",
      gallery: [
        ...generateGallery("/assets/RIPL Projects/Retrofitting/Bank of Maharashtra, Shivaji Nagar, Pune", 3),
        "/assets/RIPL Projects/Retrofitting/Bank of Maharashtra, Shivaji Nagar, Pune/4.jpg"
      ]
    },
    {
      title: "IGIDR Campus",
      location: "Goregaon, Mumbai",
      meta: "SECTOR: RETROFITTING | TYPE: INSTITUTIONAL",
      description: "Structural rehabilitation and modernization of educational research institute with enhanced safety features.",
      gallery: generateGallery("/assets/RIPL Projects/Retrofitting/IGIDR, Goregaon, Mumbai", 2)
    },
    {
      title: "IOCL Facility",
      location: "Goa",
      meta: "SECTOR: RETROFITTING | TYPE: INDUSTRIAL",
      description: "Industrial facility retrofitting ensuring compliance with modern safety standards and operational efficiency.",
      gallery: [
        "/assets/RIPL Projects/Retrofitting/IOCL Goa/Gemini_Generated_Image_1rk3w91rk3w91rk3.png",
        "/assets/RIPL Projects/Retrofitting/IOCL Goa/Gemini_Generated_Image_mhfil5mhfil5mhfi.png",
        "/assets/RIPL Projects/Retrofitting/IOCL Goa/Gemini_Generated_Image_pjc24apjc24apjc2.png"
      ]
    },
    {
      title: "Radhika Apartments",
      location: "Prabhadevi, Mumbai",
      meta: "SECTOR: RETROFITTING | TYPE: RESIDENTIAL",
      description: "Residential building retrofitting with enhanced structural safety features and modern amenities.",
      gallery: generateGallery("/assets/RIPL Projects/Retrofitting/Radhika Apartments, Prabhadevi, Mumbai", 3)
    },
    {
      title: "State Bank Bhavan",
      location: "Nariman Point, Mumbai",
      meta: "SECTOR: RETROFITTING | TYPE: LANDMARK",
      description: "Comprehensive retrofitting of iconic landmark building with modern safety standards while preserving architectural heritage.",
      gallery: [
        "/assets/RIPL Projects/Retrofitting/State Bank Bhavan, Nariman Point, Mumbai/1.jpg",
        "/assets/RIPL Projects/Retrofitting/State Bank Bhavan, Nariman Point, Mumbai/2.png",
        "/assets/RIPL Projects/Retrofitting/State Bank Bhavan, Nariman Point, Mumbai/3.jpg"
      ]
    },
    {
      title: "Uddyan Building",
      location: "Napeansea Road, Mumbai",
      meta: "SECTOR: RETROFITTING | TYPE: HERITAGE",
      description: "Heritage building restoration with modern engineering solutions ensuring long-term structural integrity.",
      gallery: generateGallery("/assets/RIPL Projects/Retrofitting/Uddyan Building, Napeansea Road, Mumbai", 2)
    }
  ];

  const project = projectsData[projectId];

  if (!project) {
    return <div className="text-white p-10">Project not found</div>;
  }

  return (
    <div id="project-detail-view" className="view-section min-h-screen py-32 bg-black transition-all duration-500">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Back Button */}
        <button
          type="button"
          onClick={onBack}
          className="group flex min-h-[44px] items-center space-x-3 py-3 text-[10px] bank-gothic text-[#c49b66] mb-12 hover:text-white transition-colors touch-manipulation"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="group-hover:-translate-x-1 transition-transform">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          <span>Back to Project Archive</span>
        </button>

        {/* Project Header */}
        <div className="mb-16">
          <span className="text-xs uppercase tracking-[0.4em] text-[#c49b66] bank-gothic block mb-4">
            {project.location}
          </span>
          <h1 className="bank-gothic text-4xl md:text-6xl text-white mb-6 leading-none">{project.title}</h1>
          <div className="structural-line my-6"></div>
          <p className="text-[10px] bank-gothic text-neutral-500 tracking-widest mb-8">{project.meta}</p>
          <p className="text-neutral-300 font-light text-sm md:text-base leading-relaxed max-w-3xl">
            {project.description}
          </p>
        </div>

        {/* 2 Column Project Image Grid */}
        <div className="py-12">
          {project.gallery.reduce((rows, image, index) => {
            if (index % 2 === 0) rows.push([]);
            rows[rows.length - 1].push(image);
            return rows;
          }, []).map((row, rowIndex) => (
            <div key={`${projectId}-row-${rowIndex}`} className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {row.map((image, colIndex) => (
                <GalleryImage
                  key={`${projectId}-${image}`}
                  imageKey={`${projectId}-${image}`}
                  src={image}
                  alt={`${project.title} ${rowIndex * 2 + colIndex + 1}`}
                  className={colIndex === 1 ? 'md:translate-y-8' : ''}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ProjectDetailPageNew;
